/* Mocked in-memory implementation of OnboardingDocumentClient for local development and UI wiring. */

import { IOnboardingDocumentClient } from './IOnboardingDocumentClient';
import { FileResponse } from './ofdTypes';

/* ---- Mock Data ---- */

interface MockDocument {
  documentId: number;
  retailerId: number;
  fileName: string;
  fileType: string;
  content: string;
}

const mockDocuments: MockDocument[] = [
  {
    documentId: 1,
    retailerId: 1,
    fileName: 'business_registration.pdf',
    fileType: 'application/pdf',
    content: 'Mock PDF content for business registration certificate',
  },
  {
    documentId: 2,
    retailerId: 1,
    fileName: 'vat_certificate.pdf',
    fileType: 'application/pdf',
    content: 'Mock PDF content for VAT certificate',
  },
  {
    documentId: 3,
    retailerId: 1,
    fileName: 'food_safety_cert.pdf',
    fileType: 'application/pdf',
    content: 'Mock PDF content for food safety certificate',
  },
  {
    documentId: 4,
    retailerId: 2,
    fileName: 'company_docs.pdf',
    fileType: 'application/pdf',
    content: 'Mock PDF content for Cork Coffee Corner documents',
  },
  {
    documentId: 5,
    retailerId: 3,
    fileName: 'galway_gourmet_license.pdf',
    fileType: 'application/pdf',
    content: 'Mock PDF content for Galway Gourmet license',
  },
];

/* ---- Mocked Client Implementation ---- */

export class OnboardingDocumentClientMocked implements IOnboardingDocumentClient {
  /**
   * Download an onboarding document (mocked - returns fake blob)
   */
  async download(documentId?: number, retailerId?: number): Promise<FileResponse> {
    await this.delay(300);

    // Find the document
    const doc = mockDocuments.find(
      (d) => d.documentId === documentId && d.retailerId === retailerId,
    );

    if (!doc) {
      // Return a generic document if not found
      const genericContent = `Mock document content for document ${documentId}, retailer ${retailerId}`;
      return {
        data: new Blob([genericContent], { type: 'application/pdf' }),
        status: 200,
        fileName: `document_${documentId}.pdf`,
        headers: {
          'content-type': 'application/pdf',
          'content-disposition': `attachment; filename="document_${documentId}.pdf"`,
        },
      };
    }

    const blob = new Blob([doc.content], { type: doc.fileType });

    console.log('[OnboardingDocumentClientMocked] Downloaded document:', doc.fileName);

    return {
      data: blob,
      status: 200,
      fileName: doc.fileName,
      headers: {
        'content-type': doc.fileType,
        'content-disposition': `attachment; filename="${doc.fileName}"`,
      },
    };
  }

  /* ---- Helper Methods ---- */

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* ---- Test Utilities ---- */

  /**
   * Get all mock documents (for testing)
   */
  getMockDocuments(): MockDocument[] {
    return [...mockDocuments];
  }

  /**
   * Add a mock document (for testing)
   */
  addMockDocument(doc: MockDocument): void {
    mockDocuments.push(doc);
  }
}
